import type { ReactNode } from 'react'
import { Link, NavLink, useMatch } from 'react-router'
import { Ellipsis, FileText, Inbox, LogOut, Megaphone, MessageSquareReply, MessageSquareText, Smartphone, Tags, UserPlus, Users, type LucideIcon } from 'lucide-react'
import { useMe } from '../api/me'
import { useSession } from '../api/session'
import { RequestFailed } from '../api/client'
import { useRealtime } from '../api/realtime'
import { useLiveState } from '../api/liveState'
import { initials } from '../lib/format'
import { NumberHealth } from '../components/NumberHealth'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

type NavItem = { to: string; label: string; icon: LucideIcon }

// The signed-in frame: a sidebar on wide screens, a bottom tab bar on phones.
// The tab bar is for top-level screens only, so it hides inside a conversation.
// Main screens and settings are kept apart: on phones the settings and the
// account live behind More, so the bar never holds more than four tabs.
export function AppLayout({ children }: { children: ReactNode }) {
  const me = useMe()
  const { signOut } = useSession()
  const inConversation = useMatch('/inbox/:conversationId') !== null
  const inSettings = useMatch('/settings/*') !== null
  const inTemplates = useMatch('/templates/*') !== null
  useRealtime(me.isSuccess)

  if (me.isPending) {
    return <p className="p-6 text-muted-foreground" role="status">Loading your workspace</p>
  }
  if (me.isError) {
    return (
      <main className="mx-auto flex max-w-md flex-col gap-4 p-6">
        <p role="alert" className="text-destructive">{(me.error as RequestFailed).message}</p>
        <Button className="self-start" onClick={signOut}>Sign in again</Button>
      </main>
    )
  }
  const { user, tenant } = me.data
  const admin = user.role === 'owner' || user.role === 'admin'
  // Tags are managed by owners and admins; the server refuses everyone else too.
  // Campaigns are for owners, admins and viewers; agents work the inbox.
  const campaigns = user.role !== 'agent'
  const main: NavItem[] = [
    { to: '/inbox', label: 'Inbox', icon: Inbox },
    { to: '/contacts', label: 'Contacts', icon: Users },
    ...(campaigns ? [{ to: '/campaigns', label: 'Campaigns', icon: Megaphone }] : []),
    { to: '/templates', label: 'Templates', icon: FileText },
  ]
  // The phone bar holds three of them plus More; the rest go into More.
  const phoneTabs = main.slice(0, 3)
  const phoneMore = main.slice(3)
  const settings: NavItem[] = [
    { to: '/settings/canned-replies', label: 'Canned replies', icon: MessageSquareText },
    { to: '/settings/auto-replies', label: 'Auto-replies', icon: MessageSquareReply },
    ...(admin ? [{ to: '/settings/tags', label: 'Tags', icon: Tags }, { to: '/settings/team', label: 'Team', icon: UserPlus }, { to: '/settings/whatsapp', label: 'WhatsApp', icon: Smartphone }] : []),
  ]
  const sideLink = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex min-h-10 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground',
      isActive && 'bg-primary-soft font-semibold text-primary-text hover:bg-primary-soft hover:text-primary-text',
    )

  return (
    <div className="flex h-dvh flex-col md:flex-row">
      <nav aria-label="Main" className="hidden w-60 shrink-0 flex-col gap-1 overflow-y-auto border-r bg-card p-3 md:flex">
        <div className="mb-3 flex items-center gap-2.5 px-2 py-1.5">
          <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
            {initials(tenant.name)}
          </span>
          <span className="min-w-0 truncate text-sm font-semibold">{tenant.name}</span>
        </div>
        {main.map((it) => (
          <NavLink key={it.to} to={it.to} className={sideLink}>
            <it.icon className="size-[18px]" aria-hidden="true" />
            {it.label}
          </NavLink>
        ))}
        <h2 className="mt-4 mb-1 px-2.5 text-xs font-semibold text-muted-foreground">Settings</h2>
        {settings.map((it) => (
          <NavLink key={it.to} to={it.to} className={sideLink}>
            <it.icon className="size-[18px]" aria-hidden="true" />
            {it.label}
          </NavLink>
        ))}
        <div className="flex-1" />
        <NumberHealth className="mb-1" />
        <LiveStatus />
        <AccountMenu name={user.name} role={user.role} onSignOut={signOut} />
      </nav>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <OfflineBanner />
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>

      {!inConversation && (
        <nav aria-label="Main" className="flex shrink-0 border-t bg-card pb-[env(safe-area-inset-bottom)] md:hidden">
          {phoneTabs.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                cn(
                  'flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-xs font-semibold text-muted-foreground',
                  isActive && 'text-primary-text',
                )
              }
            >
              <it.icon className="size-[22px]" aria-hidden="true" />
              {it.label}
            </NavLink>
          ))}
          <MoreMenu settings={[...phoneMore, ...settings]} active={inSettings || (phoneMore.length > 0 && inTemplates)} name={user.name} role={user.role} onSignOut={signOut} />
        </nav>
      )}
    </div>
  )
}

// The phone tab bar's last tab: who is signed in, number health, settings
// pages, sign out.
function MoreMenu({ settings, active, name, role, onSignOut }: { settings: NavItem[]; active: boolean; name: string; role: string; onSignOut: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-xs font-semibold text-muted-foreground',
          active && 'text-primary-text',
        )}
      >
        <Ellipsis className="size-[22px]" aria-hidden="true" />
        More
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" className="w-[min(20rem,calc(100vw-2rem))]">
        <DropdownMenuLabel className="font-normal">
          <span className="block font-semibold">{name}</span>
          <span className="block text-xs capitalize text-muted-foreground">{role}</span>
        </DropdownMenuLabel>
        <NumberHealth className="m-1" />
        <DropdownMenuSeparator />
        {settings.map((it) => (
          <DropdownMenuItem key={it.to} asChild className="min-h-11">
            <Link to={it.to}>
              <it.icon aria-hidden="true" />
              {it.label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut} className="min-h-11">
          <LogOut aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function AccountMenu({ name, role, onSignOut }: { name: string; role: string; onSignOut: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Account: ${name}, ${role}`}
        className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-accent"
      >
        <span aria-hidden="true" className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-bold text-primary-text">
          {initials(name)}
        </span>
        <span className="min-w-0 text-sm">
          <span className="block truncate font-semibold">{name}</span>
          <span className="block text-xs capitalize text-muted-foreground">{role}</span>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <span className="block font-semibold">{name}</span>
          <span className="block text-xs capitalize text-muted-foreground">{role}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut}>
          <LogOut aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Whether teammates' changes arrive by themselves. While reconnecting the
// inbox still refreshes every few seconds, so nothing is lost.
function LiveStatus() {
  const state = useLiveState()
  const label = state === 'live' ? 'Live' : state === 'offline' ? 'Reconnecting' : 'Connecting'
  const hint =
    state === 'live'
      ? 'Changes appear as they happen'
      : state === 'offline'
        ? 'Refreshing every few seconds until live updates are back'
        : 'Connecting to live updates'
  return (
    <p role="status" className="flex items-center gap-2 px-2.5 py-1.5 text-xs text-muted-foreground">
      <span aria-hidden="true" className={cn('size-2 rounded-full', state === 'live' ? 'bg-primary' : 'bg-attention-text')} />
      <span>
        <span className="font-semibold text-foreground">{label}.</span> {hint}
      </span>
    </p>
  )
}

// Phones have no sidebar, so a dropped connection gets a slim banner instead.
function OfflineBanner() {
  const state = useLiveState()
  if (state !== 'offline') return null
  return (
    <p role="status" className="bg-attention-soft px-4 py-2 text-sm text-attention-text md:hidden">
      Reconnecting. The inbox refreshes every few seconds until live updates are back.
    </p>
  )
}
